/**
 * Video Consent Alpine Component
 *
 * PURPOSE:
 * Vimeo / YouTube embeds are blocked by Cookiebot until the visitor accepts
 * marketing cookies (the iframe ships with `data-src` + `data-cookieconsent`,
 * no `src`). When consent is missing the embed renders blank, so this component
 * drives an inline consent prompt over the empty embed and, on accept, grants
 * marketing consent through Cookiebot. Cookiebot then rewrites `data-src` ->
 * `src` and fires `CookiebotOnAccept`, which reveals the video.
 *
 * USAGE (Twig): <div x-data="videoConsent"> ... </div>
 */
import { hasConsent } from '~/js/utils/datalayer'

/**
 * Minimal shape of the Cookiebot API we rely on. `submitCustomConsent` lets us
 * grant consent inline without re-opening the full cookie dialog.
 */
interface CookiebotApi {
    consent: {
        preferences: boolean
        statistics: boolean
        marketing: boolean
    }
    submitCustomConsent?: (
        preferences: boolean,
        statistics: boolean,
        marketing: boolean
    ) => void
}

export const videoConsent = () => ({
    consented: false,

    init() {
        this.consented = hasConsent('marketing')

        if (this.consented) {
            return
        }

        // Cookiebot fires this after any consent change (including our inline grant).
        window.addEventListener('CookiebotOnAccept', () => {
            if (hasConsent('marketing')) {
                this.consented = true
            }
        })
    },

    accept() {
        const cookiebot = (window as unknown as { Cookiebot?: CookiebotApi }).Cookiebot

        if (cookiebot?.submitCustomConsent) {
            const { preferences, statistics } = cookiebot.consent
            cookiebot.submitCustomConsent(preferences, statistics, true)
            return
        }

        // Cookiebot unavailable (blocked or local dev): reveal the video directly.
        this.consented = true
    },
})
